import React from 'react';
import {Container, Alert, Spinner, Button} from 'react-bootstrap';
import {useSearchParams, useNavigate} from 'react-router-dom';
import axios from 'axios';
import {Mutex} from 'async-mutex';
import {setEmailVerified} from '../auth/auth';
import {useAuth} from '../auth/auth-context';
import {getAxiosErrorMessage} from '../libs/helper';

function VerifyEmailPage(): React.JSX.Element {
  const [searchParameters] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParameters.get('token') ?? '';

  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [seconds, setSeconds] = React.useState<number>(10);
  const didVerifyRef = React.useRef<boolean>(false);
  const mutex = React.useRef(new Mutex());

  React.useEffect(() => {
    let mounted = true;
    let timerId: ReturnType<typeof globalThis.setTimeout> | undefined;
    let intervalId: ReturnType<typeof globalThis.setInterval> | undefined;

    (async () => {
      if (!token) {
        if (mounted) {
          setError('Missing token');
        }

        return;
      }

      try {
        // :NOTE: this is to keep react from calling the verify_email api twice (which happens in dev because of how developing with react works (auto mounting)...)
        await mutex.current.runExclusive(async () => {
          // Exclusive section
          if (didVerifyRef.current) {
            return;
          }

          setLoading(true);
          setError(undefined);
          await axios.post('/api/verify_email', null, {params: {token}});
          didVerifyRef.current = true;
        });

        if (!mounted) {
          return;
        }

        setEmailVerified(true);
        setSuccess(true);
        setSeconds(20);

        intervalId = globalThis.setInterval(() => {
          setSeconds(s => s - 1);
        }, 1000);

        timerId = globalThis.setTimeout(() => {
          void navigate('/', {replace: true});
        }, 10_000);
      } catch (error_: any) {
        if (!mounted) {
          return;
        }

        const message =
          getAxiosErrorMessage(error_) ?? 'Failed to verify email';
        setError(String(message));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
      if (timerId) {
        clearTimeout(timerId);
      }

      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [token, navigate]);

  return (
    <Container className='my-5'>
      <h1>Verify email</h1>
      {loading && (
        <div className='d-flex align-items-center'>
          <Spinner animation='border' />
          <span className='ms-2'>Verifying email...</span>
        </div>
      )}

      {!loading && error && <Alert variant='danger'>{error}</Alert>}

      {!loading && success && (
        <Alert variant='success'>
          Email verified successfully. Redirecting to the home page in {seconds}{' '}
          second{seconds === 1 ? '' : 's'}.
          <div className='mt-3'>
            <Button
              variant='outline-primary'
              onClick={() => {
                void navigate('/', {replace: true});
              }}
            >
              Go to home now
            </Button>
          </div>
        </Alert>
      )}

      {!loading && !success && !error && token && <p>Verifying email...</p>}
    </Container>
  );
}

export function ResendEmailVerificationPage(): React.JSX.Element {
  const {email, isAuthenticated, emailVerified} = useAuth();
  const didSendRef = React.useRef<boolean>(false);
  const mutex = React.useRef(new Mutex());

  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [success, setSuccess] = React.useState<boolean>(false);

  React.useEffect(() => {
    const mounted = true;

    (async () => {
      if (isAuthenticated && emailVerified) {
        if (mounted) {
          setError('Email is already verified.');
        }

        return;
      }

      if (!isAuthenticated || !email) {
        if (mounted) {
          setError(
            'No authenticated user email available. Please sign in first.',
          );
        }

        return;
      }

      try {
        await mutex.current.runExclusive(async () => {
          if (didSendRef.current) {
            return;
          }

          if (mounted) {
            setLoading(true);
          }

          if (mounted) {
            setError(undefined);
          }

          // Call the resend verification endpoint for the current user's email.
          await axios.post('/api/resend_email_verification', null, {
            params: {email},
          });

          didSendRef.current = true;
          if (!mounted) {
            return;
          }

          setSuccess(true);
        });
      } catch (error_: any) {
        if (!mounted) {
          return;
        }

        const message =
          getAxiosErrorMessage(error_) ?? 'Failed to send verification email';
        setError(String(message));
        setSuccess(false);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
  }, [isAuthenticated, email, emailVerified]);

  return (
    <Container className='my-5'>
      <h1>Send Email Verification</h1>

      {loading && (
        <div className='d-flex align-items-center'>
          <Spinner animation='border' />
          <span className='ms-2'>Sending verification email...</span>
        </div>
      )}

      {!loading && error && <Alert variant='danger'>{error}</Alert>}

      {!loading && success && (
        <Alert variant='success'>
          Verification email sent to <strong>{email}</strong>. Please check your
          email to verify (as well as in your spam/trash folders).
        </Alert>
      )}

      {!loading && !success && !error && (
        <p>Preparing to send verification email...</p>
      )}
    </Container>
  );
}

export default VerifyEmailPage;
